<template>
  <div class="seed-item" :class="{ 'seed-item--bloqueado': desabilitado }">
    <div class="d-flex align-items-start gap-3">
      <div class="seed-item__status pt-1">
        <i
          v-if="status === 'executando'"
          class="bi bi-arrow-repeat seed-spin text-info fs-5"
        ></i>
        <i
          v-else
          :class="[rotulo.icone, rotulo.classe, 'fs-5']"
        ></i>
      </div>

      <div class="flex-grow-1 min-w-0">
        <div class="d-flex align-items-center flex-wrap gap-2">
          <span class="fw-semibold text-body-emphasis">{{ seed.nome }}</span>
          <span :class="obterBadgeNeutro()">{{ seed.grupo === grupoMaster ? 'Master' : 'Organização' }}</span>
          <span
            v-if="seed.versao"
            class="badge bg-info bg-opacity-10 text-info border border-info-subtle"
          >v{{ seed.versao }}</span>
          <span :class="['badge', 'bg-body-secondary', 'border', 'border-secondary-subtle', rotulo.classe]">
            <i :class="[rotulo.icone, 'me-1']"></i>{{ rotulo.texto }}
          </span>
        </div>

        <div class="text-body-secondary small mt-1">{{ seed.descricao }}</div>

        <div v-if="seed.camposConfig && seed.camposConfig.length > 0" class="d-flex flex-wrap gap-1 mt-1">
          <span
            v-for="campo in seed.camposConfig"
            :key="campo.chave"
            class="badge bg-body-secondary text-body border border-secondary-subtle"
          >
            <i class="bi bi-sliders me-1"></i>{{ campo.rotuloCurto || campo.chave }}: {{ valorExibicao(campo) }}
          </span>
        </div>

        <div v-if="desabilitado && motivo" class="small mt-1 text-warning">
          <i class="bi bi-lock-fill me-1"></i>{{ motivo }}
        </div>
      </div>

      <div class="seed-item__acoes d-flex flex-wrap gap-1 justify-content-end">
        <button
          v-if="seed.camposConfig && seed.camposConfig.length > 0"
          class="btn btn-sm btn-outline-secondary"
          title="Configurações do seed"
          @click="emitir('configurar')"
        >
          <i class="bi bi-gear"></i>
        </button>

        <button
          class="btn btn-sm btn-primary"
          :disabled="desabilitado"
          :title="desabilitado ? motivo : 'Executar seed'"
          @click="emitir('executar')"
        >
          <i v-if="status === 'executando'" class="spinner-border spinner-border-sm me-1"></i>
          <i v-else class="bi bi-play-fill me-1"></i>Executar
        </button>

        <button
          v-if="seed.endpoints?.reconciliar"
          class="btn btn-sm btn-outline-primary"
          :disabled="desabilitado"
          :title="desabilitado ? motivo : 'Reconciliar (classes C# → banco de dados)'"
          @click="emitir('reconciliar')"
        >
          <i class="bi bi-arrow-repeat me-1"></i>Reconciliar
        </button>

        <button
          v-if="seed.endpoints?.verificar"
          class="btn btn-sm btn-outline-info"
          :disabled="desabilitado"
          :title="desabilitado ? motivo : 'Verificar alterações no banco'"
          @click="emitir('verificar')"
        >
          <i class="bi bi-search me-1"></i>Verificar
        </button>

        <button
          v-if="seed.endpoints?.exportar"
          class="btn btn-sm btn-outline-secondary"
          :disabled="desabilitado"
          :title="desabilitado ? motivo : 'Exportar (banco de dados → classes C#)'"
          @click="emitir('exportar')"
        >
          <i class="bi bi-upload me-1"></i>Exportar
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { rotuloStatus, GRUPO_MASTER } from '@/constants/seedsCatalog'
import { useUI } from '@/composables/useUI'
import { formatarDataBR } from '@/utils/useDataBR'

const props = defineProps({
  seed: { type: Object, required: true },
  status: { type: String, default: 'nao_rastreado' },
  desabilitado: { type: Boolean, default: false },
  motivo: { type: String, default: '' },
  config: { type: Object, default: () => ({}) }
})

const emit = defineEmits(['acao'])

const { obterBadgeNeutro } = useUI()
const grupoMaster = GRUPO_MASTER

const rotulo = computed(() => rotuloStatus(props.status))

function emitir (tipo) {
  emit('acao', { seed: props.seed, tipo })
}

function valorExibicao (campo) {
  const valor = props.config?.[campo.chave] ?? campo.padrao

  if (campo.tipo === 'booleano' || !campo.tipo) {
    return valor ? 'Sim' : 'Não'
  }

  if (campo.tipo === 'select') {
    const opcao = (campo.opcoes || []).find(o => o.id === valor)
    return opcao ? opcao.label : (valor != null && valor !== '' ? String(valor) : '—')
  }

  if (campo.tipo === 'data') {
    return valor ? formatarDataBR(valor) : '—'
  }

  return valor != null && valor !== '' ? String(valor) : '—'
}
</script>

<style scoped>
.seed-item {
  padding: 0.75rem 0.85rem;
  border: 1px solid var(--bs-border-color);
  border-radius: 0.5rem;
  background-color: var(--bs-body-bg);
  transition: background-color 0.2s ease, border-color 0.2s ease;
}

.seed-item + .seed-item {
  margin-top: 0.5rem;
}

.seed-item:hover {
  border-color: var(--bs-primary-border-subtle);
  background-color: var(--bs-tertiary-bg);
}

.seed-item--bloqueado {
  opacity: 0.7;
}

.seed-item__acoes {
  flex-shrink: 0;
}

.min-w-0 {
  min-width: 0;
}

.seed-spin {
  display: inline-block;
  animation: seed-spin 1s linear infinite;
}

@keyframes seed-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
